import { APIModel } from '@shared/interfaces';

import { Language } from '@/organisations/models';

import { QuestionDisplayType } from './question-display-type.enum';
import { QuestionField } from './question-field.model';
import { QuestionTitle } from './question-title.model';

export class Question implements APIModel {
    id = -1;
    published = true;
    organisation = -1;
    company?: number;
    order = 0;
    titles = [] as QuestionTitle[];
    localizedTitle = new QuestionTitle();
    isOrganisationQuestion = false;
    useCount?: number;

    localize(language: Language) {
        if (this.titles.length > 0) {
            this.localizedTitle =
                this.titles.find((title) => title.language == language) ??
                this.titles.find((label) => label.language == Language.NL) ??
                this.titles[0];
        }
    }

    deserialize(data: any) {
        this.id = data.id ?? -1;
        this.published = data.published ?? true;
        this.titles = data.titles?.map((title: any) => new QuestionTitle().deserialize(title)) ?? [];
        this.organisation = data.organisation ?? undefined;
        this.company = data.company ?? undefined;
        this.order = data.order ?? 0;
        this.isOrganisationQuestion = this.organisation !== undefined && this.company == undefined;
        this.useCount = data.use_count ?? undefined;
        return this;
    }

    serialize() {
        return {
            titles: this.titles.map((title) => title.serialize()),
            'is_organisation_question': this.isOrganisationQuestion,
            published: this.published,
        };
    }

    clone() {
        let quest = new Question().deserialize(this.serialize());
        Object.assign(quest, {
            id: this.id,
            organisation: this.organisation,
            company: this.company,
            isOrganisationQuestion: this.isOrganisationQuestion,
            localizedTitle: this.localizedTitle,
            useCount: this.useCount,
        });
        return quest;
    }

    static Sort(q1: Question, q2: Question): number {
        return (
            Number(!q1.company) - Number(!q2.company) ||
            Number(!q1.organisation) - Number(!q2.organisation) ||
            q1.order - q2.order ||
            q1.localizedTitle.title.localeCompare(q2.localizedTitle.title)
        );
    }
}

export class NestedQuestion implements APIModel {
    id = -1;
    titles = [] as QuestionTitle[];
    inputFields?: QuestionField[];
    required = false;
    multi = false;
    displayType: QuestionDisplayType = QuestionDisplayType.Vertical;
    localizedTitle = new QuestionTitle();
    hasNestedQuestions = false;
    multiLimit?: number;
    autoCollapse = false;

    localize(language: Language) {
        if (this.titles.length > 0) {
            this.localizedTitle =
                this.titles.find((title) => title.language == language) ??
                this.titles.find((label) => label.language == Language.NL) ??
                this.titles[0];
        }
        this.inputFields?.forEach((field) => field.localize(language));
    }

    deserialize(data: any) {
        this.id = data.id ?? -1;
        this.titles = data.titles?.map((title: any) => new QuestionTitle().deserialize(title)) ?? [];
        this.inputFields = data.input_fields?.map((field: any) => new QuestionField().deserialize(field));
        this.inputFields?.sort((a, b) => a.order - b.order);
        this.multi = data.multi ?? false;
        this.required = data.required ?? false;
        this.displayType = data.display_type ?? QuestionDisplayType.Vertical;
        this.hasNestedQuestions = this.inputFields?.some((field) => field.nestedQuestion !== undefined) ?? false;
        this.multiLimit = data.multi_limit ?? undefined;
        this.autoCollapse = data.auto_collapse ?? false;
        this.updateDisplayType();
        return this;
    }

    private updateDisplayType(): void {
        // Overrides the display type for nested questions:
        // - if a question has nested questions, it should be shown vertically
        // - all the nested questions should be show horizontally (unless, recursively, they have nested questions)
        if (this.hasNestedQuestions) {
            this.displayType = QuestionDisplayType.Vertical;
            this.inputFields
                ?.filter((field) => field.nestedQuestion !== undefined)
                .forEach((field) => {
                    field.nestedQuestion!.updateDisplayType();
                });
        }
    }
}
